import { CommonModule, Location } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, ViewChild, inject } from "@angular/core";
import { Router } from "@angular/router";
import { ContextmenuComponent } from "@app/shared/contextmenu/contextmenu.component";
import { ContextMenuItem, MenuItemBuilder } from "@app/shared/contextmenu/menu-item";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { MonitoringService } from "@app/shared/services/monitoring.service";
import { TabEventListenerType } from "@enums/TabEventListenerType.enum";
import { copyText } from "@methods/CommonMethods";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { ToastrService } from "ngx-toastr";
import { PageLookup } from "../../common/constants/pages/page-lookup";
import { Pages } from "../../common/constants/pages/Pages";
import { guid, isGuid } from "../../common/methods/uniqueMethods";
import { PageMapping } from "../../common/models/generic/TabModels/PageMapping";
import { TabMenuItem } from "../../common/models/generic/TabModels/TabMenuItem";
import { TabModel } from "../../common/models/generic/TabModels/TabModel";
import { LocalStorageService } from "../../services/http/LocalStorageService";
import { PermissionsService } from "../../services/permissions/PermissionsService";
import { TabService } from "../../services/tabs/TabService";
import { MicroservicePermissionService } from "@services/permissions/permissions.service";
import { TabMenuComponent } from "./tabmenu/tabmenu.component";
import { TuckedMenuComponent } from "./tuckedmenu/tuckedmenu.component";
import { TabPaneComponent } from "./tab-pane/tab-pane.component";
import { SafePipe } from "@app/shared/pipes/safe.pipe";

declare var $: any;

@Component({
    selector: "cerrix-tabs",
    templateUrl: "./tab.component.html",
    styleUrls: ["./tab.component.scss"],
    imports: [CommonModule, ContextmenuComponent, TabMenuComponent, TuckedMenuComponent, TabPaneComponent, SafePipe]
})
export class TabComponent implements AfterViewInit {
    private pages = inject(Pages);
    private _pageLookup = inject(PageLookup);
    _tabService = inject(TabService);
    private toastr = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _permService = inject(PermissionsService);
    private _localStorageService = inject(LocalStorageService);
    private _settingsDataService = inject(SettingsDataService);
    private monitoringService = inject(MonitoringService);
    private _menuItemBuilder = inject(MenuItemBuilder);

    private static readonly localStorageKeyCerrixTabs: string = "cerrix-tabs";
    private static readonly localStorageKeyCerrixTabsActive: string = "cerrix-tabs-active";
    private static readonly localStorageKeyCerrixTabMenuPinned: string = "cerrix-tab-menu-pinned";

    private microservicePermissionService: MicroservicePermissionService = inject(
        MicroservicePermissionService
    );

    public tabs: TabModel[] = [];
    public activeTab: string;
    public renderedTabs: string[] = [];

    public Overflow = false;

    @ViewChild("contextMenuCmp", { static: false })
    public contextMenuComponent: ContextmenuComponent;

    contextMenuItem: TabModel;
    public contextMenu: ContextMenuItem[];

    private tabElement: Element;
    private isFirstOpenTab = true;
    // Reference count instead of a boolean: a popstate can trigger async sub-page restore
    // (e.g. the dashboard hook awaits a fetch) that emits activeTabMenuChangedEvent only after
    // the handler returns. The counter lets that async work hold suppression open until it
    // resolves, so no duplicate history entry is pushed.
    private urlSyncSuppressCount = 0;

    private location: Location = inject(Location);
    private router = inject(Router);
    private cdr = inject(ChangeDetectorRef);

    constructor() {
        // Read the tuck state before the first change detection pass. ngAfterViewInit runs
        // after the template is checked, so setting it there flipped an already-checked
        // [class.tucked] binding and threw NG0100.
        this._tabService.sideMenuTucked =
            this._localStorageService.getItem<boolean>(
                TabComponent.localStorageKeyCerrixTabMenuPinned
            ) ?? true;

        this.createContextMenu();
    }

    public ngAfterViewInit() {
        this.tabElement = document.getElementsByClassName("cerrix-tab")[0];
        this._tabService.addTabEvent.subscribe((value) => {
            this.addTab(value, true);
        });
        this._tabService.addTabFromUrlEvent.subscribe((value) => {
            this.processUrlTabModel(value);
        });

        this._tabService.storeTabChangesEvent.subscribe(() => {
            this.storeTabsSession();
            this.updateCurrentDocumentTitle();
        });

        this._tabService.closeTabByGuid.subscribe((value) => {
            const params = value.split("|");
            const tab = this.getTabByID(params[0]);
            const undoable = params.length > 1 && params[1] == "true";

            tab.close(undoable);
        });

        this._tabService.renameTabEvent.subscribe((value) => {
            const params = value.split("|");
            this.renameTab(this.getTabByID(params[0]), params[1].toString());
        });

        this._tabService.refreshTabEvent.subscribe((page) => this.refreshTab(page));

        this._tabService.activeTabMenuChangedEvent.subscribe((identifier) => {
            if (identifier === this.activeTab) {
                this.syncUrlToActiveTab(this.getTabByID(identifier));
            }
        });

        // Snapshot the persisted session before bring-up. processUrlTab below re-adds the
        // active tab (its path is in the address bar after a refresh), and that addTab
        // overwrites cerrix-tabs/cerrix-tabs-active. Capture the previous session first so
        // "continue where you left off" can still restore every tab.
        const savedTabs = this._localStorageService.getItem<TabModel[]>(
            TabComponent.localStorageKeyCerrixTabs
        );
        const savedActiveTab = this._localStorageService.getItem<string>(
            TabComponent.localStorageKeyCerrixTabsActive
        );

        // During bring-up the address bar already holds the correct URL (root or a deeplink).
        // Suppress URL sync so activating the Home tab does not overwrite a deeplink before we
        // read it back with processUrlTab.
        const initialUrl = window.location.origin + this.location.path();
        this.urlSyncSuppressCount++;

        // create Home tab
        this.createInternalTab(this.pages.Dashboard, null, false, true);

        // Seed a /dashboard?activemenu=<guid> deeplink onto the Home tab so the dashboard
        // manager restores the linked dashboard instead of the default on load.
        this.seedDashboardDeepLink(initialUrl);

        this.processUrlTab(initialUrl);
        this.urlSyncSuppressCount--;

        this.checkCookieTabs(savedTabs, savedActiveTab, true);
        this.addOverflowListener();

        // Browser Back/Forward: activate the matching tab (or reopen it from the URL).
        this.location.subscribe(() => this.handlePopState());

        // The bring-up above (createInternalTab/processUrlTab/checkCookieTabs) mutates tabs,
        // renderedTabs and activeTab from inside this AfterView hook, after Angular's first
        // check of this view. Flush that into the view now, so the dev-mode recheck sees a
        // stable render instead of throwing NG0100.
        this.cdr.detectChanges();
    }

    public processUrlTabModel(tab: TabModel) {
        // We should check if we got a legacy url/or a new url
        const url = tab.url;
        if (url && url.indexOf("/") >= 0) {
            const split = url.split("/");
            const tag = url.indexOf("/") === 0 ? split[1] : split[0];

            const exactName = this.pages.getExactName(tag);
            if (exactName) {
                this.processUrlTab(url, tab);
                return;
            }
        }
        this.addTab(tab, true);
    }

    public processUrlTab(url: string, tabWithBrokenUrl?: TabModel) {
        let path = ""; // window.location.pathname.toLowerCase();
        let search = "";

        if (url) {
            let parsed: URL;
            try {
                parsed = new URL(url);
            } catch (error) {
                try {
                    let root = window.location.origin;
                    if (root[root.length - 1] !== "/") {
                        root += "/";
                    }

                    parsed = new URL(root + url);
                } catch (err) {}
            }

            if (parsed) {
                path = parsed.pathname;
                search = parsed.search;
            }
        }

        if (!path) {
            setTimeout(() =>
                // Toastr will give a console error when not set in a setTimeout (combination of showing in AfterViewInit)
                // https://github.com/scttcper/ngx-toastr/issues/160
                this.toastr.error(
                    `Could not find a page with the name: "${path}"`,
                    "Page not found!",
                    {
                        tapToDismiss: true,
                    }
                )
            );
        }

        const hasValue = path.replace("/", "").trim();
        if (hasValue) {
            const tab = this.getTabFromUrl(path, search);
            // The Dashboard path resolves to no tab (the Home tab already exists and a specific
            // dashboard is restored from the activemenu query), so there is nothing to add.
            if (!tab) {
                return;
            }
            if (tabWithBrokenUrl) {
                tabWithBrokenUrl.url = tab.url;
            } else {
                tabWithBrokenUrl = tab;
            }
            this.addTab(tabWithBrokenUrl, true);
        }
    }

    public getTabFromUrl(url: string, search: string): TabModel {
        let path = url.replace("//", "/");
        if (path[0] === "/") {
            path = path.substring(1);
        }

        let pathID: any;
        const fullPath = path;
        const slLoc = path.indexOf("/");
        if (slLoc >= 0) {
            const split = path.split("/");
            path = slLoc === 0 ? split[1] : split[0];
            pathID = split.length > 1 ? split[split.length - 1] : undefined;
        }

        if (path.length > 0 && path !== this.pages.Dashboard.toLowerCase()) {
            const lookup = this.pages.getExactName(path);
            if (lookup) {
                const mapping = this.getLookup(lookup);
                if (mapping) {
                    const tab: TabModel = {
                        identifier: guid(),
                        name: lookup,
                        lookupname: lookup,
                        closable: true,
                        id: pathID,
                        _fullPath: fullPath,
                    };

                    // this is for old urls. Since we dont know how to hande the params just pass them through.
                    if (mapping.Url) {
                        if (!search) {
                            search = "";
                        }
                        tab.url = mapping.Url;
                        if (tab.id) {
                            tab.url += "?id=" + tab.id;
                            if (search && search[0] === "?") {
                                search = "&" + search.substr(1, search.length);
                            }
                        }
                        tab.url += search;
                    } else if (search && search.length > 0) {
                        if (search[0] === "?") {
                            search = search.slice(1);
                        }

                        const config: { [key: string]: string } = {};
                        const configVariables = search.split("&").filter((x) => x.indexOf("=") > 0);
                        configVariables.forEach((cv) => {
                            const kv = cv.split("=");
                            // Decode once here: buildTabPath encoded these values with
                            // encodeURIComponent, so config holds the raw value the rest of the
                            // app compares against (e.g. activemenu vs a menu item id).
                            config[kv[0]] = this.safeDecodeURIComponent(kv[1]);
                        });

                        // A bare-array config is serialized as a single delimited `ids` param
                        // (buildTabPath); rebuild the array so the page receives the same
                        // string[] it was opened with from the grid.
                        const keys = Object.getOwnPropertyNames(config);
                        tab.config =
                            keys.length === 1 && keys[0] === "ids"
                                ? config["ids"].split(",")
                                : config;
                    }

                    return tab;
                }
            }
        }
        if (!path || path === this.pages.Dashboard.toLowerCase()) {
            return;
        }
        setTimeout(() =>
            // Toastr will give a console error when not set in a setTimeout (combination of showing in AfterViewInit)
            // https://github.com/scttcper/ngx-toastr/issues/160
            this.toastr.error(`Could not find a page with the name: "${path}"`, "Page not found!", {
                tapToDismiss: true,
            })
        );
    }

    public createInternalTab(name: string, paramID: any, closable: boolean, activate: boolean) {
        const tab: TabModel = {
            identifier: guid(),
            name,
            lookupname: name,
            closable,
            id: paramID,
        };

        this.addTab(tab, activate);
    }

    public getLookup(lookupName: string): PageMapping {
        const page = this._pageLookup[lookupName] as PageMapping;
        return page;
    }

    // Decode a single URL query value, falling back to the raw value when the address bar
    // holds malformed percent-encoding so bring-up never crashes.
    private safeDecodeURIComponent(value: string): string {
        try {
            return decodeURIComponent(value);
        } catch {
            return value;
        }
    }

    public addTab(tab: TabModel, activate?: boolean) {
        // Clean tab from previous session
        tab = JSON.parse(JSON.stringify(tab));

        if (tab.lookupname !== this.pages.Dashboard) {
            tab.closable = true;
        }

        this.bindDefaults(tab);

        if (tab.lookupname === "oldpage") {
            if (this.openDuplicate("name", tab)) {
                return;
            }

            this.tabs.push(tab);
            if (activate) {
                this.openTab(tab.identifier);
            }
            return;
        }

        const lookup = this.getLookup(tab.lookupname);

        // This will be called after the permissions check beneath this method declaration.
        const continueAdd = () => {
            let continueAdding = lookup.NoDuplicate !== true;
            if (!continueAdding) {
                if (
                    lookup.CheckDuplicateOnGuid === true
                        ? this.openDuplicate("id", tab)
                        : this.openDuplicate("lookupname", tab)
                ) {
                    if (lookup.IsWorkspace) {
                        this._tabService.handleWorkspaceDuplicate(tab);
                    } else if (lookup.ListenToConfigChangedEvent) {
                        this._tabService.handleConfigChangedEvent(tab);
                    }

                    return;
                } else {
                    continueAdding = true;
                }
            }

            if (continueAdding) {
                tab.comp = lookup.Component;
                if (!tab.url) {
                    tab.url = lookup.Url;
                    if (tab.url && tab.id) {
                        tab.url += "?id=" + tab.id;
                    }
                }

                if (lookup.ComponentConfig) {
                    if (!tab.config) {
                        // only override tabconfig if it is empty.
                        tab.config = JSON.parse(JSON.stringify(lookup.ComponentConfig));
                    }
                }

                tab.showLoader = !!lookup.ShowLoader;

                if (tab.url && this.openDuplicate("url", tab)) {
                    return;
                }

                if (tab.id && this.openDuplicate("id", tab)) {
                    return;
                }

                // Tabs whose state is a bare array (e.g. a grid selection serialized to
                // ?ids=<guid>,<guid>) have no url or id to dedupe on. Two with the same serialized
                // path are the same tab (e.g. a refresh bring-up next to the session-restored
                // copy), so focus the existing one; a different selection keeps a separate tab.
                if (Array.isArray(tab.config)) {
                    const tabPath = this.buildTabPath(tab);
                    const existing =
                        tabPath && this.tabs.find((t) => this.buildTabPath(t) === tabPath);
                    if (existing) {
                        this.openTab(existing.identifier);
                        return;
                    }
                }

                this.tabs.push(tab);
                if (tab.lookupname !== this.pages.Dashboard) {
                    this.storeTabsSession();
                }

                if (activate) {
                    this.openTab(tab.identifier);
                }
            }

            this.isOverflowing();
        };

        const handleFeatureToggleSetting = () => {
            if (lookup.FeatureToggleSetting) {
                this._settingsDataService
                    .getSetting(lookup.FeatureToggleSetting)
                    .subscribe((setting) => {
                        if (lookup.FeatureToggleCustomComponentSetter) {
                            const compToSet = lookup.FeatureToggleCustomComponentSetter(
                                setting.BoolValue
                            );

                            if (compToSet) {
                                lookup.Component = compToSet;
                                tab.comp = compToSet;
                            }
                        } else {
                            if (setting.BoolValue) {
                                lookup.Url = null;
                                tab.url = null;
                            } else {
                                lookup.Component = null;
                                tab.comp = null;
                            }
                        }
                        continueAdd();
                    });
            } else {
                continueAdd();
            }
        };

        if (lookup && (lookup.PermissionProperty || lookup.microserviceModule)) {
            if (lookup.microserviceModule) {
                if (
                    !this._permService.permissions[lookup.PermissionProperty] ||
                    !this.microservicePermissionService.hasModuleAccess(lookup.microserviceModule)
                ) {
                    this.denyAccess(tab.lookupname);
                    return;
                }
                handleFeatureToggleSetting();
            } else {
                // Check in permissions if user has access.
                const permCheck = () => {
                    let permModel: any = this._permService.permissions;
                    let hasAccess = false;

                    const checks = lookup.PermissionProperty.split(".");
                    checks.forEach((check, index) => {
                        if (index === checks.length - 1) {
                            hasAccess = permModel[check];
                        } else {
                            permModel = permModel[check];
                        }
                    });

                    if (!hasAccess) {
                        this.denyAccess(tab.lookupname);
                        return;
                    }
                    handleFeatureToggleSetting();
                };

                if (!this._permService.permissionsLoaded) {
                    this._permService.awaitedMethods.push(permCheck);
                } else {
                    permCheck();
                }
            }
        } else {
            handleFeatureToggleSetting();
        }
    }

    private denyAccess(tabName: string): void {
        this.toastr.error(`Not enough rights!`, `You have no rights to open ${tabName}`, {
            tapToDismiss: true,
        });
    }

    public openDuplicate(duplicateProp: string, tab: TabModel): boolean {
        const duplicateTabs = this.tabs.filter(
            (t) => t.lookupname === tab.lookupname && t[duplicateProp] === tab[duplicateProp]
        );
        if (duplicateTabs.length > 0) {
            this.openTab(duplicateTabs[0].identifier);
            return true;
        }

        return false;
    }

    public bindDefaults(tab: TabModel) {
        tab.parent = this.getTabByID(this.activeTab);

        // This will change 'undefined' to 'null', which will prevent a lot of console errors.
        if (!tab.menu) {
            tab.menu = null;
        }

        if (tab.url) {
            tab.refresh = () => {
                this.renderedTabs.splice(this.renderedTabs.indexOf(tab.identifier), 1);
                const temp = tab.url;
                tab.url = "";
                setTimeout(() => {
                    tab.url = temp;
                    this.openTab(tab.identifier);
                }, 100);
            };
        }

        if (!tab.close && tab.closable) {
            tab.close = (undoable?: boolean) => {
                if (undoable === null || undoable === undefined) {
                    undoable = true;
                }
                this.closeTab(tab, undoable);
                this._tabService.listeners.triggerListener(TabEventListenerType.OnClose, tab);
                this._tabService.listeners.removeListeners(tab);
            };
        }

        if (!tab.activate) {
            tab.activate = () => {
                this.openTab(tab.identifier);
                this._tabService.listeners.triggerListener(TabEventListenerType.OnFocus, tab);
            };
        }

        if (!tab.copyNameToClipboard) {
            const that = this;
            tab.copyNameToClipboard = function () {
                copyText(this.name);
                that.toastr.success("Tab name copied to clipboard", "Copy name");
            };
        }

        if (!tab.copyUrlToClipboard) {
            const that = this;
            tab.copyUrlToClipboard = function () {
                const path = that.buildTabPath(this);
                // The default Dashboard has no path but is still linkable via the site root.
                if (!path && this.lookupname !== that.pages.Dashboard) {
                    return;
                }

                copyText(window.location.origin + (path ?? "/"));
                that.toastr.success("Url copied to clipboard", "Copy URL");
            };
        }
    }

    /**
     * Serializes an addressable tab to a lowercased path+query (`/<PageKey>/<id>?<config>`).
     * Returns null for non-addressable tabs (Dashboard/Home, legacy iframe-only tabs).
     */
    public buildTabPath(tab: TabModel): string | null {
        if (!tab || !tab.lookupname) {
            return null;
        }

        // The Dashboard tab is addressable only when a specific (non-default) dashboard is
        // selected; the default dashboard keeps the clean root URL. The generic code below
        // then serializes config.activemenu into /dashboard?activemenu=<guid>.
        if (tab.lookupname === this.pages.Dashboard && !(tab.config && tab.config.activemenu)) {
            return null;
        }

        const lookupKey = this.pages.getReversed(tab.lookupname);
        if (!lookupKey) {
            return null;
        }

        let path = "/" + lookupKey;

        if (tab.id && (!isNaN(+tab.id) || isGuid(tab.id))) {
            path += "/" + tab.id;
        }

        if (tab.config) {
            let query: string;
            if (Array.isArray(tab.config)) {
                // A bare-array config (e.g. a grid selection of control ids) round-trips as a
                // single delimited `ids` param. Serializing each index instead
                // (?0=<guid>&1=<guid>&length=N) produced a malformed query the backend rejects.
                const items = tab.config.filter(
                    (v) =>
                        typeof v === "string" || typeof v === "number" || typeof v === "boolean"
                );
                query = items.length ? `ids=${encodeURIComponent(items.join(","))}` : "";
            } else {
                // Only URL-safe primitives belong in the address bar. Workspace tabs use `config`
                // as a runtime state bag (functions, objects, null), which must not be serialized.
                query = Object.getOwnPropertyNames(tab.config)
                    .filter((k) => {
                        const t = typeof tab.config[k];
                        return t === "string" || t === "number" || t === "boolean";
                    })
                    .map((k) => `${k}=${encodeURIComponent(tab.config[k])}`)
                    .join("&");
            }
            if (query) {
                path += "?" + query;
            }
        }

        return path.toLowerCase();
    }

    /**
     * Reads an `activemenu` guid from a `/dashboard?activemenu=<guid>` deeplink and stores it
     * on the Home tab's config, so the dashboard manager restores the linked dashboard on
     * load. No-op for the clean root URL or any non-dashboard URL.
     */
    private seedDashboardDeepLink(url: string) {
        let parsed: URL;
        try {
            parsed = new URL(url);
        } catch {
            return;
        }

        const normalized = parsed.pathname.replace(/^\//, "").split("/")[0].toLowerCase();
        if (normalized && normalized !== this.pages.Dashboard.toLowerCase()) {
            return;
        }

        const activemenu = parsed.searchParams.get("activemenu");
        if (!activemenu) {
            return;
        }

        const home = this.tabs.find((t) => t.lookupname === this.pages.Dashboard);
        if (home) {
            // searchParams.get already decoded the value once, which matches buildTabPath's single
            // encodeURIComponent, so store it as-is (config.activemenu is the raw dashboard guid).
            home.config = { ...(home.config ?? {}), activemenu };
        }
    }

    private async handlePopState() {
        const path = this.location.path();

        // Root or the dashboard path -> activate the Home tab and re-select the dashboard the
        // URL points at (or the default dashboard for the clean root URL).
        const normalized = path.replace(/^\//, "").split("?")[0];
        if (!normalized || normalized === this.pages.Dashboard.toLowerCase()) {
            const home = this.tabs.find((t) => t.lookupname === this.pages.Dashboard);
            if (home) {
                this.urlSyncSuppressCount++;
                try {
                    this.openTab(home.identifier);
                    // Await so async sub-page restore (e.g. the dashboard hook fetching a
                    // non-sidebar dashboard) stays suppressed until its emit has fired.
                    await this.restoreSubPageFromUrl(home, path);
                } finally {
                    this.urlSyncSuppressCount--;
                }
            }
            return;
        }

        // Workspace config keeps mutating (filters/preset), so its query can drift after the
        // URL was pushed. Match on the stable query-less identity so Back switches tabs
        // instead of reopening.
        const identity = path.split("?")[0];
        const existing = this.tabs.find(
            (t) => (this.buildTabPath(t) ?? "").split("?")[0] === identity
        );
        this.urlSyncSuppressCount++;
        try {
            if (existing) {
                this.openTab(existing.identifier);
                // Query-less identity matched, so an open detail tab keeps its current sub-page.
                // Reflect the sub-page carried in the URL query so Back/Forward also steps
                // through the sidebar navigation, not just between tabs.
                await this.restoreSubPageFromUrl(existing, path);
            } else if (!this.isRouterOwnedRoute(identity)) {
                // Tab was closed since; reopen it from the URL via the deep-link path.
                // (getTabFromUrl seeds config.activemenu, so the sub-page restores on menu init.)
                // Router-owned routes (e.g. /login) are skipped above so Back to the login
                // page does not raise a spurious "page not found" toast.
                this.processUrlTab(window.location.origin + path);
            }
        } finally {
            this.urlSyncSuppressCount--;
        }
    }

    /**
     * True when the URL points at a standalone Angular route (e.g. /login) instead of a tab.
     * Those routes are owned by the router, so on Back/Forward the tab host must not try to
     * reopen them as a tab (that would raise a "page not found" toast). Read from the route
     * table, so adding a standalone route needs no change here. The "" (root) and "**"
     * (catch-all) routes are the tab host itself and are excluded.
     */
    private isRouterOwnedRoute(identity: string): boolean {
        const tag = identity.replace(/^\//, "").split("/")[0].toLowerCase();
        if (!tag) {
            return false;
        }
        return this.router.config.some(
            (route) => route.path && route.path !== "**" && route.path.toLowerCase() === tag
        );
    }

    /**
     * Navigates an open tab's sidebar to the sub-page named by the URL's `activemenu`
     * query, or to its first sub-page when the query is absent. No-op for tabs without a
     * menu (Dashboard, legacy iframe tabs).
     */
    private async restoreSubPageFromUrl(tab: TabModel, path: string): Promise<void> {
        const menu = tab.menu;
        if (!menu || !menu.menuItems || menu.menuItems.length === 0 || !menu.menuItemClicked) {
            return;
        }

        const qIndex = path.indexOf("?");
        const search = qIndex >= 0 ? path.substring(qIndex + 1) : "";
        // URLSearchParams decodes the value once, matching the single encodeURIComponent
        // buildTabPath applies. config.activemenu is stored raw, so both sides compare cleanly.
        const activemenu = new URLSearchParams(search).get("activemenu");

        // The tab already shows the sub-page the URL points at, so skip re-applying it. Re-running
        // the hook would reset workspace filters/sort/page and rebuild every dashboard widget (and
        // could raise the unsaved-changes prompt) on a Back/Forward that did not change the
        // sub-page. Case-insensitive because buildTabPath lowercases the query.
        const current = tab.config?.activemenu ?? null;
        if ((activemenu ?? "").toLowerCase() === (current ?? "").toLowerCase()) {
            return;
        }

        if (activemenu) {
            // Some menus (dashboards) can resolve an id that is not a sidebar item by fetching it.
            // Await so the caller can keep URL sync suppressed across the async resolution.
            if (menu.selectMenuItemById) {
                await menu.selectMenuItemById(activemenu);
                return;
            }

            const target = menu.getTabMenuItemByMenuItemId(activemenu, true);
            if (target) {
                menu.menuItemClicked(target);
            }
            return;
        }

        // No activemenu -> the base URL. Preset workspaces expose a selectDefaultMenuItem hook
        // to re-select their default preset; other menus fall back to their first sub-page.
        if (menu.selectDefaultMenuItem) {
            await menu.selectDefaultMenuItem();
            return;
        }

        const first = this.firstClickableMenuItem(menu.menuItems);
        if (first) {
            menu.menuItemClicked(first);
        }
    }

    private firstClickableMenuItem(items: TabMenuItem[]): TabMenuItem | undefined {
        for (const item of items) {
            // Only menu-system sub-pages (which carry a menuItemId and are the only menus
            // that push ?activemenu URLs) are safe to auto-activate. Preset-workspace and
            // form-detail menus expose obj-based action items with no menuItemId; clicking
            // one on Back/Forward would fire the action (e.g. open the Preset settings modal).
            if (item.clickable !== false && item.menuItemId) {
                return item;
            }
            if (item.children) {
                const nested = this.firstClickableMenuItem(item.children);
                if (nested) {
                    return nested;
                }
            }
        }

        return undefined;
    }

    //#region TabManagement

    public mouseClick(event: MouseEvent, tab: TabModel) {
        // This indicates a click on the close button, so that button has its own implementation.
        if (event.button === 0 && $(event.target).is("i")) {
            return;
        }

        switch (event.button) {
            case 0: // Main (left) button
                tab.activate();
                break;
            case 1: // Middle (scroll) button
                if (tab.closable) {
                    tab.close(true);
                }
                break;
        }

        this.isOverflowing();
    }

    public openTab(identifier: string) {
        const tab = this.tabs.filter((t) => t.identifier === identifier)[0];
        if (!tab) {
            return;
        }

        if (this.renderedTabs.indexOf(identifier) < 0) {
            this.renderedTabs.push(identifier);
            if (tab.url && tab.url.length > 0) {
                tab.showLoader = true;
                const loadingPrompt = this._promptService.loader(
                    "Loading " + (tab.name ? tab.name : "")
                );

                const interval = setInterval(() => {
                    const iframe = $(`#iframe-${identifier}`)[0];
                    const document = iframe.contentDocument || iframe.contentWindow.document;
                    if (document && document.body) {
                        const body = $.trim(document.body.innerHTML).length;
                        if (body > 0) {
                            if (this.isIframeDuplicateOfParent(tab, document)) {
                                this.closeTab(tab, false);
                                this.toastr.error(
                                    "The selected page is not available anymore.",
                                    "Page not found!",
                                    { tapToDismiss: true }
                                );
                            }

                            loadingPrompt.close();
                            tab.showLoader = false;

                            clearInterval(interval);

                            const outerRef = this;
                            $(iframe).on("load", function () {
                                // See if the iframe body contains an app-root selector (only loaded in with the application)
                                const isRoot =
                                    $.trim(this.contentDocument.body).length === 0 ||
                                    $(this.contentDocument.body).find("app-root").length > 0;
                                if (isRoot) {
                                    // Document is redirected to root, so close tab and redirect to home tab.
                                    outerRef.closeTab(tab, false);
                                }
                            });
                        }
                    }
                }, 1000);
            }
        }

        const oldTab = this.getTabByID(this.activeTab);
        this.activeTab = identifier;

        this._tabService.listeners.triggerListener(TabEventListenerType.OnBlur, oldTab);
        this._tabService.listeners.triggerListener(TabEventListenerType.OnFocus, tab);

        this._tabService.activeTab = tab;
        this.updateDocumentTitle(tab.name);
        this.logTabView(tab);

        // Do not store active tab on page load to keep previous session
        if (this.isFirstOpenTab) {
            this.isFirstOpenTab = false;
        } else {
            this._localStorageService.setUserItem(
                TabComponent.localStorageKeyCerrixTabsActive,
                identifier
            );
        }

        this.syncUrlToActiveTab(tab);
    }

    /**
     * Reflects the active tab in the address bar, pushing a history entry so browser
     * Back/Forward steps through visited tabs. Skipped while handling a popstate event
     * (suppressUrlSync) and when the URL already matches (avoids duplicate entries).
     */
    private syncUrlToActiveTab(tab: TabModel) {
        if (this.urlSyncSuppressCount > 0) {
            return;
        }

        const path = this.buildTabPath(tab) ?? "/";
        if (path === this.location.path() || (path === "/" && !this.location.path())) {
            return;
        }

        this.location.go(path);
    }

    public renameTab(tab: TabModel, newTabName: string) {
        tab.name = newTabName;
        return tab;
    }

    public refreshTab(page: string) {
        const tab = this.tabs.find((t) => t.lookupname === page);
        if (tab) {
            tab.refresh();
        }
    }

    public closeTab(tab: TabModel, allowUndo?: boolean) {
        const undoIndex = this.tabs.indexOf(tab);
        if (undoIndex > 0) {
            if (tab.beforeClose != null && !tab.beforeClose()) {
                return;
            }

            const continueClose = () => {
                const undoTab = this.tabs.splice(undoIndex, 1)[0];
                const renderedIndex = this.renderedTabs.indexOf(undoTab.identifier);
                if (renderedIndex >= 0) {
                    this.renderedTabs.splice(renderedIndex, 1);
                }

                if (tab.identifier === this.activeTab) {
                    this.openTab(this.tabs[undoIndex - 1].identifier);
                }

                this.storeTabsSession();

                if (tab.afterClose != null) {
                    tab.afterClose();
                }

                if (allowUndo) {
                    const toast = this.toastr.info(
                        `Click here to reopen '${undoTab.name}'`,
                        "Tab closed",
                        {
                            progressAnimation: "decreasing",
                            timeOut: 5000,
                            closeButton: true,
                        }
                    );
                    toast.onTap.subscribe(() => {
                        this.addTab(undoTab);
                        this.tabs.splice(undoIndex, 0, this.tabs.pop());
                        this.openTab(undoTab.identifier);
                        this.storeTabsSession();
                    });
                }
            };

            const lookup = this.getLookup(tab.lookupname);
            if (lookup && lookup.ShowWarningOnClose) {
                // check permission to show message
                if (
                    !lookup.ShowWarningPermissionProperty ||
                    this._permService.permissions[lookup.ShowWarningPermissionProperty]
                ) {
                    this._promptService
                        .confirm(
                            "Close tab",
                            tab.lookupname +
                                " will be closed. All changes will be discarded. Continue?"
                        )
                        .onConfirm()
                        .subscribe(() => {
                            continueClose();
                        });
                }
            } else {
                continueClose();
            }
        }
    }

    public closeTabs() {
        this._promptService
            .confirm("Close all tabs", "Close all open tabs?")
            .onConfirm()
            .subscribe(() => {
                // Close closable tabs
                const closedTabs: TabModel[] = [];
                for (let i = this.tabs.length - 1; i >= 0; i--) {
                    const tab = this.tabs[i];
                    if (tab.closable) {
                        // Store a sanitized copy so a live tab's non-serializable/circular
                        // runtime state (e.g. an incident tab's circular "blueprint") cannot
                        // crash the JSON clone in addTab when the tabs are reopened.
                        closedTabs.splice(0, 0, this.sanitizeTab(tab)); // We are reversing through the tabs, so add them in reversed order in the list.
                        this.closeTab(tab, false);
                    }
                }

                const toast = this.toastr.info(
                    "Click here to reopen closed tabs",
                    "All tabs closed",
                    {
                        progressAnimation: "decreasing",
                        timeOut: 5000,
                        closeButton: true,
                    }
                );
                toast.onTap.subscribe(() => {
                    // On tab reopen closed tabs
                    this.reopenTabsSession(closedTabs, this.tabs[0].identifier);
                });

                this.isOverflowing();
            });
    }

    public getTabByID(id: string): TabModel {
        return this.tabs.find((t) => t.identifier === id);
    }

    //#endregion

    //#region Side Menu

    public sideMenuTuckToggle() {
        this._tabService.sideMenuTucked = !this._tabService.sideMenuTucked;

        this._localStorageService.setUserItem(
            TabComponent.localStorageKeyCerrixTabMenuPinned,
            this._tabService.sideMenuTucked
        );
    }

    //#endregion

    //#region Tabs session

    public checkCookieTabs(tabs: TabModel[], activeTab: string, saveNewTabs: boolean) {
        if (
            tabs &&
            tabs.length > 1 &&
            tabs[0].name.toLowerCase() === this.pages.Dashboard.toLowerCase()
        ) {
            setTimeout(() =>
                // Toastr will give a console error when not set in a setTimeout (combination of showing in AfterViewInit)
                // https://github.com/scttcper/ngx-toastr/issues/160
                this.toastr
                    .info("Continue where you left off?", "", {
                        progressAnimation: "decreasing",
                        timeOut: 10000,
                        extendedTimeOut: 5000,
                        closeButton: true,
                    })
                    .onTap.subscribe(() => {
                        this.reopenTabsSession(tabs, activeTab);

                        if (saveNewTabs) {
                            this.storeTabsSession();
                        }
                    })
            );
        } else {
            this.storeTabsSession();
        }
    }

    public reopenTabsSession(tabs: TabModel[], activeTab?: string) {
        tabs.forEach((tab) => {
            // Isolate each tab so one tab that fails to reopen (e.g. a non-serializable
            // clone) does not abort the loop and block the remaining tabs.
            try {
                this.addTab(
                    tab,
                    tabs.length === 1 || (activeTab && activeTab === tab.identifier)
                );
            } catch (error) {
                console.error(`Could not reopen tab "${tab.name}"`, error);
            }
        });
    }

    /** Strip a tab down to the serializable fields needed to reopen it. */
    private sanitizeTab(t: TabModel): TabModel {
        return <TabModel>{
            identifier: t.identifier,
            name: t.name,
            lookupname: t.lookupname,
            id: t.id,
            url: t.url,
            config: t.config ? JSON.parse(JSON.stringify(t.config)) : null,
        };
    }

    public storeTabsSession() {
        const copies = this.tabs.map((t) => this.sanitizeTab(t));

        const tabs = copies || "";
        const activeTab = this.activeTab;

        this._localStorageService.setUserItem(TabComponent.localStorageKeyCerrixTabs, tabs);
        this._localStorageService.setUserItem(
            TabComponent.localStorageKeyCerrixTabsActive,
            activeTab
        );
    }

    //#endregion

    public canCloseTabs(): boolean {
        return this.tabs.every((t) => t.beforeClose == null || t.beforeClose(true));
    }

    //#region ScrollTab
    public scrollTab(value) {
        this.tabElement.scrollLeft += value;
    }

    public isOverflowing() {
        setTimeout(() => {
            this.Overflow = this.tabElement.scrollWidth > this.tabElement.clientWidth;
        }, 0);
    }

    public addOverflowListener() {
        window.addEventListener(
            "resize",
            (e) => {
                this.isOverflowing();
            },
            false
        );
    }

    // Check if the opened iFrame is the same as the parent
    // which is the case if the iFrame redirected the user back to start
    public isIframeDuplicateOfParent(tab: TabModel, document: any): boolean {
        const doc = document.body.ownerDocument;
        const iframeWindow = doc.defaultView || doc.parentWindow;
        if (
            iframeWindow &&
            iframeWindow.location &&
            top &&
            top.location &&
            iframeWindow.location.pathname == "/" &&
            top.location.pathname == "/"
        ) {
            return true;
        }

        return false;
    }
    //#endregion

    public isTabClosable = (item: TabModel): boolean => {
        return item.closable;
    };

    private updateCurrentDocumentTitle() {
        const currentTab = this.getTabByID(this.activeTab);
        if (currentTab) {
            this.updateDocumentTitle(currentTab.name);
        }
    }

    private updateDocumentTitle(title: string) {
        let newTitle = "CERRIX";
        if (title) {
            newTitle += ` - ${title}`;
        }

        document.title = newTitle;
    }

    private logTabView(tab: TabModel) {
        let url = tab.url;
        if (!url && tab.lookupname) {
            url = this.pages.getReversed(tab.lookupname);
            if (url && tab.id) {
                url += `/${tab.id}`;
            }
        }

        if (url && !url.startsWith(window.location.origin)) {
            url = `${window.location.origin}/${url}`.replace(/([^:]\/)\/+/g, "$1");
        }

        this.monitoringService.logPageView(tab.lookupname, url);
    }

    public onContextMenu($event: MouseEvent, item: TabModel): void {
        this.contextMenuItem = item;
        this.contextMenuComponent.openMenuAtMousePosition($event);
        $event.stopPropagation();
    }

    public trackByIdentifier(index: number, item: TabModel): string {
        return item.identifier;
    }

    private createContextMenu() {
        this.contextMenu = this._menuItemBuilder
            .appendItem("Reload tab", () => this.contextMenuItem.refresh(), null, "fas fa-sync")
            .appendItem(
                "Copy name",
                () => this.contextMenuItem.copyNameToClipboard(),
                null,
                "fas fa-copy fa-fw"
            )
            .appendItem(
                "Copy URL",
                () => this.contextMenuItem.copyUrlToClipboard(),
                null,
                "fas fa-link fa-fw"
            )
            .appendDivider()
            .appendItem(
                "Close tab",
                () => this.contextMenuItem.close(),
                null,
                "fas fa-times fa-fw",
                () => this.contextMenuItem && this.contextMenuItem.closable
            )
            .appendItem(
                "Close all",
                () => this.closeTabs(),
                null,
                "fas fa-times-circle fa-fw",
                () => this.tabs.length > 2
            )
            .build();
    }
}
